import React, { createContext, useContext, useReducer, useEffect } from 'react';
import axios from 'axios';
import axiosWithAuth from '../utils/axiosWithAuth';
import API_BASE_URL from '../config/api';

const AuthContext = createContext();

const initialState = {
    user: null,
    token: localStorage.getItem('token'),
    isAuthenticated: false,
    loading: true,
    error: null
};

const authReducer = (state, action) => {
    switch (action.type) {
        case 'LOGIN_START':
            return { ...state, loading: true, error: null };
        case 'LOGIN_SUCCESS':
            return {
                ...state,
                user: action.payload.user,
                token: action.payload.token,
                isAuthenticated: true,
                loading: false,
                error: null
            };
        case 'LOGIN_FAILURE':
            return {
                ...state,
                user: null,
                token: null,
                isAuthenticated: false,
                loading: false,
                error: action.payload
            };
        case 'LOGOUT':
            return {
                ...state,
                user: null,
                token: null,
                isAuthenticated: false,
                loading: false,
                error: null
            };
        case 'UPDATE_USER':
            return {
                ...state,
                user: action.payload
            };
        case 'SET_LOADING':
            return {
                ...state,
                loading: action.payload
            };
        default:
            return state;
    }
};

export const AuthProvider = ({ children }) => {
    const [state, dispatch] = useReducer(authReducer, initialState);

    // Note: Token handling is now done by axiosWithAuth interceptor
    // No need to manually set axios defaults

    // Check if user is authenticated on app load
    useEffect(() => {
        const checkAuth = async () => {
            if (state.token) {
                try {
                    const response = await axiosWithAuth.get(`/auth/me`);
                    dispatch({
                        type: 'LOGIN_SUCCESS',
                        payload: {
                            user: response.data,
                            token: state.token
                        }
                    });
                } catch (error) {
                    localStorage.removeItem('token');
                    dispatch({ type: 'LOGOUT' });
                }
            } else {
                dispatch({ type: 'SET_LOADING', payload: false });
            }
        };

        checkAuth();
    }, []);

    const login = async (email, password) => {
        dispatch({ type: 'LOGIN_START' });

        try {
            const response = await axios.post(`${API_BASE_URL}/auth/login`, {
                email,
                password
            });

            const { access_token, user } = response.data;
            localStorage.setItem('token', access_token);

            dispatch({
                type: 'LOGIN_SUCCESS',
                payload: {
                    user,
                    token: access_token
                }
            });

            return { success: true };
        } catch (error) {
            const errorMessage = error.response?.data?.detail || 'Login failed';
            dispatch({
                type: 'LOGIN_FAILURE',
                payload: errorMessage
            });
            return { success: false, error: errorMessage };
        }
    };

    const register = async (userData) => {
        dispatch({ type: 'LOGIN_START' });

        try {
            const response = await axios.post(`${API_BASE_URL}/auth/register`, userData);

            const { access_token, user } = response.data;
            localStorage.setItem('token', access_token);

            dispatch({
                type: 'LOGIN_SUCCESS',
                payload: {
                    user,
                    token: access_token
                }
            });

            return { success: true };
        } catch (error) {
            const errorMessage = error.response?.data?.detail || 'Registration failed';
            dispatch({
                type: 'LOGIN_FAILURE',
                payload: errorMessage
            });
            return { success: false, error: errorMessage };
        }
    };

    const logout = () => {
        localStorage.removeItem('token');
        dispatch({ type: 'LOGOUT' });
    };

    const updateUser = (userData) => {
        dispatch({
            type: 'UPDATE_USER',
            payload: userData
        });
    };

    const value = {
        ...state,
        login,
        register,
        logout,
        updateUser
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export default AuthContext; 